import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredWorks } from '@/mocks/films';

export default function HeroCarousel() {
  const slides = featuredWorks;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <section className="flex w-full items-center justify-center bg-primary-500 px-4 py-24">
        <div className="text-center text-background-50">
          <i className="ri-film-line text-3xl" />
          <p className="mt-3 text-base">暂无焦点内容，敬请期待最新影视资讯</p>
        </div>
      </section>
    );
  }

  const go = (dir: number) => setIndex((i) => (i + dir + slides.length) % slides.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[440px] w-full md:h-[600px]">
        {slides.map((work, i) => (
          <div
            key={work.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={work.backdrop}
              alt={`${work.title} 剧照`}
              title={`${work.title} 影视作品专题`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
          </div>
        ))}

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-7xl flex-col justify-end px-4 pb-14 md:px-8 md:pb-20">
            <div className="max-w-2xl animate-fade-up text-background-50" key={index}>
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold">
                <i className="ri-fire-fill text-xs" />
                本期焦点
              </span>
              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight md:text-5xl">
                {slides[index].title}
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-50/85">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-300" />
                  {slides[index].rating.toFixed(1)}
                </span>
                <span>{slides[index].year}</span>
                <span>{slides[index].region}</span>
                <span>{slides[index].genres.join(' · ')}</span>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-50/80 md:text-base">
                {slides[index].synopsis}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${slides[index].id}`}
                  className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看剧情详情
                </Link>
                <Link
                  to={`/category/${slides[index].type}`}
                  className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
                >
                  更多{slides[index].type === 'movie' ? '电影' : '剧集'}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一个"
          className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一个"
          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 md:left-auto md:right-8 md:translate-x-0">
          {slides.map((work, i) => (
            <button
              key={work.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到 ${work.title}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}